import { CAMPAIGN_ENUM_TYPES } from '../../../shared/constants'
import { baseConvert } from './baseConverter'

export const isDefined = val => val !== undefined && val !== null && val !== ''

export const convertStringToNumber = (
  str,
  { convertUnitsToCents, isInteger } = {},
) => {
  const parsedValue = parseFloat(str)

  if (isNaN(parsedValue)) return null

  const convertedValue = convertUnitsToCents ? parsedValue * 100 : parsedValue

  return isInteger ? Math.floor(convertedValue) : Number(convertedValue)
}

export const convertGraphQlCampaignToFormValues = campaign => ({
  pitchUrl: campaign.pitchUrl,
  countryId: campaign.country ? campaign.country.id : null,
  payoutPerConversion:
    campaign.bonusEnabled && isDefined(campaign.payoutPerConversion)
      ? campaign.payoutPerConversion
      : null,
  bonusEnabled: !!campaign.bonusEnabled,
  bonusLimit:
    campaign.bonusEnabled && isDefined(campaign.bonusLimit)
      ? campaign.bonusLimit
      : null,
  baRedemptionPeriod:
    campaign.bonusEnabled && isDefined(campaign.baRedemptionPeriod)
      ? Math.floor(campaign.baRedemptionPeriod / 7)
      : 2,
  name: campaign.name,
  status: campaign.status,
  type: campaign.type,
  clientId: campaign.client ? campaign.client.id : null,
  printingCostPerFlyer: campaign.printingCostPerFlyer,
  distributionCost: campaign.distributionCost,
  basePayout: campaign.basePayout,
})

export const parseCreateCampaignFormValues = values => ({
  ...parseCampaignData(values),
  clientId: values.clientId,
  countryId: values.countryId,
  type: values.type,
  metrics: Array.isArray(values.metrics)
    ? values.metrics.map(m => ({
        name: m.name,
        isPrimary: !!m.isPrimary,
        targetConversionRate: isDefined(m.targetConversionRate)
          ? convertStringToNumber(m.targetConversionRate)
          : null,
        targetCostPerMetric: isDefined(m.targetCostPerMetric)
          ? convertStringToNumber(m.targetCostPerMetric, {
              convertUnitsToCents: true,
              isInteger: true,
            })
          : null,
      }))
    : [],
})

export const parseUpdateCampaignFormValues = (campaignId, values) => ({
  ...parseCampaignData(values),
  campaignId,
  metrics: Array.isArray(values.metrics)
    ? values.metrics.map(m => ({
        id: m.id,
        name: m.name,
        isPrimary: !!m.isPrimary,
        targetConversionRate: isDefined(m.targetConversionRate)
          ? convertStringToNumber(m.targetConversionRate)
          : null,
        targetCostPerMetric: isDefined(m.targetCostPerMetric)
          ? convertStringToNumber(m.targetCostPerMetric, {
              convertUnitsToCents: true,
              isInteger: true,
            })
          : null,
      }))
    : [],
  status: values.status,
  pitchUrl: values.pitchUrl,
})

const parseCampaignData = values => ({
  name: values.name,
  printingCostPerFlyer: values.printingCostPerFlyer,
  baRedemptionPeriod: isDefined(values.baRedemptionPeriod)
    ? values.baRedemptionPeriod * 7
    : null,
  bonusEnabled: values.bonusEnabled,
  payoutPerConversion: values.bonusEnabled
    ? checkIfDefinedAndConvertToCents(values.payoutPerConversion)
    : null,
  bonusLimit: values.bonusEnabled
    ? checkIfDefinedAndConvertToCents(values.bonusLimit)
    : null,
  distributionCost: checkIfDefinedAndConvertToCents(values.distributionCost),
  basePayout: checkIfDefinedAndConvertToCents(values.basePayout),
})

const checkIfDefinedAndConvertToCents = value =>
  isDefined(value)
    ? convertStringToNumber(value, {
        convertUnitsToCents: true,
        isInteger: true,
      })
    : null

export const getInitialFormValues = (campaign, editMode) => {
  if (editMode) {
    return campaign
      ? { ...convertGraphQlCampaignToFormValues(campaign), editMode }
      : null
  }

  return {
    bonusEnabled: false,
    baRedemptionPeriod: 2,
    type: CAMPAIGN_ENUM_TYPES.HANDTOHAND,
  }
}

/**
 * @typedef CampaignQuoteDetails
 * @property {string} type Either 'HANDTOHAND' or 'LETTERBOX'
 * @property {number} printingCostPerFlyer In micro cents
 * @property {number} redemptionTime In weeks
 * @property {number} distributionCost In cents
 * @property {number|null} bonusLimit In cents, null or 0 if bonus isn't enabled
 * @property {number|null} bonusPerConversion In cents, null or 0 if bonus isn't enabled
 * @property {Array<GeoJSONFeature>} missions The mission boundaries as a
 * GeoJSON Polygon
 */

/**
 * Get a url safe string of representing campaignd details
 * @param {CampaignQuoteDetails} details
 * @returns {string} url safe string
 */
export const convertCampaignDetailsToURLString = function(details) {
  var bits = convertCampaignDetailsToBits(details)
  return baseConvert(
    bits,
    '01',
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.',
  )
}

/**
 * Get a url safe string of representing campaignd details
 * @param {CampaignQuoteDetails} details
 * @returns {string} url safe string
 */
export const convertURLStringToCampaignDetails = function(urlString) {
  var bits = baseConvert(
    urlString,
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.',
    '01',
  )
  return convertBitsToCampaignDetails(bits)
}

/**
 * @param {CampaignQuoteDetails} details
 * @returns {string} bits
 */
export const convertCampaignDetailsToBits = function(details) {
  const {
    printingCostPerFlyer = 0,
    redemptionTime = 2,
    distributionCost = 0,
    bonusLimit = 0,
    payoutPerConversion = 0,
    missions = [],
    type,
    countryId,
    ...otherProps
  } = details

  if (Object.keys(otherProps).length)
    throw new Error(`Unorecognised props ${Object.keys(otherProps).join(',')}.`)

  var allBits = ''
  // We'll make the first bit a version, if its a 0 then we know it needs to
  // be parsed differently, if its a 1 then we'll parse it this way, allows
  // us to change in the future while remaining backwards compatible.
  allBits += '1'

  // Then we'll use a single bit for H2H vs letterbox
  switch (type) {
    case 'HANDTOHAND':
      allBits += '0'
      break
    case 'LETTERBOX':
      allBits += '1'
      break
    default:
      throw new Error('Unrecognised type.')
  }

  // We'll store cost per flyer from a value between 1 dollar and 0 micro cents
  // e.g. 0 to 10000. We'll need 14 bits for this
  allBits += leadZeros(Number(printingCostPerFlyer).toString(2), 14).slice(
    0,
    14,
  )

  // Redemption period only be 1 to 6 weeks currently. We'll use 6 bits
  allBits += leadZeros(Number(redemptionTime).toString(2), 3).slice(0, 3)

  // For distribution cost will allow up to $1000 in cents
  allBits += leadZeros(Number(distributionCost).toString(2), 17).slice(0, 17)

  // For the bonus limit we'll allow up to $1 million dollars 100 mil cents,
  // i.e. 0 to 100000000
  allBits += leadZeros(Number(bonusLimit).toString(2), 27).slice(0, 27)

  // For bonus per conversion will allow up to $100 in cents
  allBits += leadZeros(Number(payoutPerConversion).toString(2), 14).slice(0, 14)

  // Country id. We'll use 8 bits
  allBits += leadZeros(Number(countryId).toString(2), 8).slice(0, 8)
  // All of the next bits represent the missions
  // We'll only do latitude and longitude to 5 decimal places, this is
  // accurate to about 10cm
  // Each 26 block will be longitude, then the next 25 bits will be latitude
  var boundaryBits = ''
  missions.forEach(m => {
    if (m.geometry.type != 'Polygon')
      throw new Error('Only supports Geo JSON polygons.')

    // Use the first 8 bits to store the number of coordinates in this polygon
    if (m.geometry.coordinates.length > 256)
      throw new Error('Polygons with more than 256 coordinates not supported.')
    boundaryBits += leadZeros(
      Number(m.geometry.coordinates.length).toString(2),
      8,
    ).slice(0, 8)
    m.geometry.coordinates.forEach(coords => {
      boundaryBits += convertLongitudeToBits(coords[0])
      boundaryBits += convertLatitudeToBits(coords[1])
    })
  })

  // TODO Reorder string to put most likely bits that are to be 0 at the front
  return allBits + boundaryBits
}

/**
 * Handles version choose for converting bits back into a campaign details object.
 * @param {string} bits
 * @returns {CampaignQuoteDetails}
 */
export const convertBitsToCampaignDetails = bits => {
  const versionBit = bits.substr(0, 1)
  if (versionBit == 1) return convertBitsToCampaignDetailsLogic(bits)
  throw new Error(`Version not supported. ${versionBit}`)
}

/**
 * Convert bits back into a campaign details object. V2
 * @param {string} bits
 * @returns {CampaignQuoteDetails}
 */
const convertBitsToCampaignDetailsLogic = bits => {
  const countryIdBits = bits.substr(77, 8)
  const missionsBits = bits.substr(85)
  const quoteDetails = baseCampaignBitsToObject(bits)

  return {
    ...quoteDetails,
    countryId: parseInt(countryIdBits, 2),
    missions: missionsBitsToObject(missionsBits),
  }
}

/**
 * Convert base campaign bits back into a campaign details object.
 * @param {string} bits
 * @returns {CampaignQuoteDetails}
 */
const baseCampaignBitsToObject = bits => {
  var typeBit = bits.substr(1, 1)
  var costPerFlyerBits = bits.substr(2, 14)
  var redemptionTimeBits = bits.substr(16, 3)
  var distributionCostBits = bits.substr(19, 17)
  var bonusLimitBits = bits.substr(36, 27)
  var bonusPerConversionBits = bits.substr(63, 14)

  const type = typeBit == 0 ? 'HANDTOHAND' : typeBit == 1 ? 'LETTERBOX' : null

  if (!type) throw new Error('The type bit makes no sense.')

  const quoteDetails = {
    type,
    printingCostPerFlyer: parseInt(costPerFlyerBits, 2),
    redemptionTime: parseInt(redemptionTimeBits, 2),
    distributionCost: parseInt(distributionCostBits, 2),
    bonusLimit: parseInt(bonusLimitBits, 2),
    payoutPerConversion: parseInt(bonusPerConversionBits, 2),
  }

  if (quoteDetails.bonusLimit == 0) delete quoteDetails.bonusLimit
  if (quoteDetails.payoutPerConversion == 0)
    delete quoteDetails.payoutPerConversion

  return quoteDetails
}
const missionsBitsToObject = missionsBits => {
  let result = []
  while (missionsBits.length) {
    var totalPolygonsBits = missionsBits.substr(0, 8)
    missionsBits = missionsBits.slice(8)

    var mission = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [],
      },
    }

    var totalPolygons = parseInt(totalPolygonsBits, 2)
    for (var i = 0; i < totalPolygons; i++) {
      var longitudeBits = missionsBits.substr(0, 26)
      missionsBits = missionsBits.slice(26)
      var latitudeBits = missionsBits.substr(0, 25)
      missionsBits = missionsBits.slice(25)
      var longitude = convertBitsToLongitude(longitudeBits)
      var latitude = convertBitsToLatitude(latitudeBits)
      mission.geometry.coordinates.push([longitude, latitude])
    }

    result = result || []
    result.push(mission)
  }
  return result
}
/**
 * Pads the string with leading zeros until the string matches the provided
 * length
 * @param {string} str
 * @param {number} length
 * @returns {string}
 */
const leadZeros = function(str, length) {
  while (str.length < length) {
    str = '0' + str
  }
  return str
}

/**
 * This is lossy down to about 10cm of accuracy.
 */
const convertLatitudeToBits = function(latitude) {
  // Latitude is -90 to 90
  latitude += 90 // Ensure it is an integer
  if (latitude < 0 || latitude > 180) throw new Error('Invalid latitude.')
  latitude = Math.round(latitude * Math.pow(10, 5))
  return leadZeros(Number(latitude).toString(2), 25).slice(0, 25)
}

/**
 * This is lossy down to about 10cm of accuracy.
 */
const convertLongitudeToBits = function(longitude) {
  // Longitude is -180 to 180
  longitude += 180 // Ensure it is an integer
  if (longitude < 0 || longitude > 360) throw new Error('Invalid longitude.')
  longitude = Math.round(longitude * Math.pow(10, 5))
  return leadZeros(Number(longitude).toString(2), 26).slice(0, 26)
}

/**
 * Convert a bit representation of longitude back to the original longitude
 * value.
 * @param {string}
 * @returns {number}
 */
const convertBitsToLongitude = function(bits) {
  var value = parseInt(bits, 2)
  value = Math.round(value - 180 * Math.pow(10, 5)) / Math.pow(10, 5)
  return value
}

/**
 * Convert a bit representation of latitude back to the original latitude
 * value.
 * @param {string}
 * @returns {number}
 */
const convertBitsToLatitude = function(bits) {
  var value = parseInt(bits, 2)
  value = Math.round(value - 90 * Math.pow(10, 5)) / Math.pow(10, 5)
  return value
}



// WEBPACK FOOTER //
// ./src/Containers/Campaigns/CreateEditCampaign/util.js